import React, { useState } from 'react';
import { useGetIntegrations } from '@pages/Integrationsv3/hooks';
import { useCommerceFromStore } from '@store/actions/commerce';
import { useMainPrinter } from '@store/actions/electronPrintSettings';
import { useElectronPrintersFromStore } from '@store/actions/electronPrinters';
import { ModuleName } from '@utils/constants';
import { useElectron } from '@utils/hooks/useElectron';
import useModules from '@utils/hooks/useModules';
import { useSubscription } from '@pages/Subscriptionsv2/hooks/useSubscriptions';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBillingConfig } from '@pages/Commerce/hooks/commerce';
import { useSetupValidation } from '@pages/Setup/hooks/useSetupValidation';
import { useShouldShowSetup } from '@pages/Setup/hooks/useShouldShowSetup';
import { plickTheme } from '@theme/index';
import {
  IconBrandWhatsappFilled,
  IconMapPinFilled,
  IconTruckDelivery,
  IconCreditCardFilled,
  IconClockFilled,
  IconChevronDown,
  IconChevronUp,
  IconAlertTriangleFilled,
  IconInfoCircleFilled,
  IconCircleCheckFilled,
} from '@tabler/icons-react';

const ERROR_TYPES_MAP = {
  error: {
    key: 'error',
    icon: <IconAlertTriangleFilled />,
    color: plickTheme.colors.warning,
  },
  info: {
    key: 'info',
    icon: <IconInfoCircleFilled />,
    color: plickTheme.colors.info,
  },
  warning: {
    key: 'warning',
    icon: <IconAlertTriangleFilled />,
    color: plickTheme.colors.warning,
  },
  success: {
    key: 'success',
    icon: <IconCircleCheckFilled />,
    color: plickTheme.colors.success,
  },
} as const;

type IWarning = {
  message: string | React.ReactNode;
  onClick: () => void;
  type: keyof typeof ERROR_TYPES_MAP;
  emoji?: string | React.ReactNode;
  show: boolean;
  bgColor?: string;
  hideIcon?: boolean;
};

const useStatus = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const commerce = useCommerceFromStore();
  const { hasModule } = useModules();
  const { isElectronInstance } = useElectron();
  const printers = useElectronPrintersFromStore();
  const electronPrinterName = useMainPrinter().electronPrinterName;
  const integrations = useGetIntegrations();
  const hasPaymentIntegrations = integrations.integrations.some(
    (integration) => integration.provider_type === 'payment',
  );
  const { lastSuscriptionInvoice } = useSubscription();
  const { handleBillingRequest } = useBillingConfig();

  // Setup validation
  const setupStatus = useSetupValidation(commerce);
  const isOnSetupPage = location.pathname === '/configuracion-inicial';
  const shouldShowSetup = useShouldShowSetup();

  const shouldShowSubscriptionWarning = (invoice?: { status?: string; attempt_count?: number }): boolean => {
    return invoice?.status === 'uncollectible' || (invoice?.status === 'open' && (invoice.attempt_count ?? 0) > 0);
  };

  // All warnings with explicit priority levels (lower number = higher priority)
  const allWarnings: Array<IWarning & { priority: number }> = [
    // ========================================
    // PRIORIDADES 1-5: CONFIGURACIÓN INICIAL (SETUP)
    // ========================================
    // Estas son las configuraciones esenciales que se manejan en /configuracion-inicial
    // Solo se muestran si NO estamos en esa página

    // PRIORITY 1: WhatsApp (CRÍTICO)
    {
      priority: 1,
      message: 'Configura tu número de WhatsApp para recibir pedidos.',
      onClick: () => {
        if (shouldShowSetup) {
          navigate('/configuracion-inicial');
        } else {
          navigate('/datos-del-comercio');
        }
      },
      type: ERROR_TYPES_MAP.error.key,
      emoji: <IconBrandWhatsappFilled />,
      show: !setupStatus.whatsapp.isComplete && !isOnSetupPage,
    },
    // PRIORITY 2: Dirección del negocio (CRÍTICO)
    {
      priority: 2,
      message: 'Agrega la dirección de tu comercio.',
      onClick: () => {
        if (shouldShowSetup) {
          navigate('/configuracion-inicial');
        } else {
          navigate('/datos-del-comercio/ubicacion');
        }
      },
      type: ERROR_TYPES_MAP.error.key,
      emoji: <IconMapPinFilled />,
      show: !setupStatus.address.isComplete && !isOnSetupPage,
    },
    // PRIORITY 3: Métodos de entrega (CRÍTICO)
    {
      priority: 3,
      message: 'Selecciona al menos un método de entrega.',
      onClick: () => {
        if (shouldShowSetup) {
          navigate('/configuracion-inicial');
        } else {
          navigate('/tarifas');
        }
      },
      type: ERROR_TYPES_MAP.error.key,
      emoji: <IconTruckDelivery />,
      show: !setupStatus.deliveryMethods.isComplete && !isOnSetupPage,
    },
    // PRIORITY 4: Métodos de pago (CRÍTICO)
    {
      priority: 4,
      message: 'Configura los métodos de pago.',
      onClick: () => {
        if (shouldShowSetup) {
          navigate('/configuracion-inicial');
        } else {
          navigate('/tarifas');
        }
      },
      type: ERROR_TYPES_MAP.error.key,
      emoji: <IconCreditCardFilled />,
      show: !setupStatus.paymentMethods.isComplete && !isOnSetupPage,
    },
    // PRIORITY 5: Horarios de atención (ALTO)
    {
      priority: 5,
      message: 'Define los horarios de atención.',
      onClick: () => {
        if (shouldShowSetup) {
          navigate('/configuracion-inicial');
        } else {
          navigate('/horarios');
        }
      },
      type: ERROR_TYPES_MAP.warning.key,
      emoji: <IconClockFilled />,
      show: !setupStatus.schedules.isComplete && !isOnSetupPage,
    },

    // ========================================
    // PRIORIDADES 6+: ERRORES OPERATIVOS/TÉCNICOS
    // ========================================

    // PRIORITY 6: Subscription payment issues (CRÍTICO FINANCIERO)
    {
      priority: 6,
      message: (
        <>
          Problema con el pago de tu suscripción.{' '}
          <span className="bg-color-white text-color-primary_dark rounded-lg px-2">CLICK AQUI</span>
        </>
      ),
      onClick: () => handleBillingRequest(),
      type: ERROR_TYPES_MAP.error.key,
      show: shouldShowSubscriptionWarning(lastSuscriptionInvoice),
    },
    // PRIORITY 7: Payment provider integration missing
    {
      priority: 7,
      message: 'Conecta un proveedor para activar el pago en línea.',
      onClick: () => navigate('/integraciones'),
      type: ERROR_TYPES_MAP.error.key,
      show:
        !hasPaymentIntegrations &&
        setupStatus.paymentMethods.isComplete &&
        (commerce.metodos_pago_envio_domicilio.payment_provider ||
          commerce.metodos_pago_comer_restaurant.payment_provider ||
          commerce.metodos_pago_pasar_recoger.payment_provider),
    },
    // PRIORITY 8: No shipping method configured
    {
      priority: 8,
      message: 'No tienes un método de envío seleccionado.',
      onClick: () => navigate('/tarifas'),
      type: ERROR_TYPES_MAP.error.key,
      show: Boolean(
        hasModule(ModuleName.TARIFAS) &&
          setupStatus.deliveryMethods.isComplete &&
          commerce.activar_envio_domicilio &&
          !commerce.tipo_envio_domicilio,
      ),
    },
    // PRIORITY 9: Printer not found
    {
      priority: 9,
      message: 'Impresora principal no encontrada.',
      onClick: () => navigate('/ajustes'),
      type: ERROR_TYPES_MAP.error.key,
      show:
        isElectronInstance &&
        Boolean(electronPrinterName) &&
        printers.length > 0 &&
        printers.filter((printer) => printer.name === electronPrinterName).length === 0,
    },
    // PRIORITY 10: No main printer configured
    {
      priority: 10,
      message: 'Configura una impresora principal.',
      onClick: () => navigate('/ajustes'),
      type: ERROR_TYPES_MAP.error.key,
      show: isElectronInstance && !electronPrinterName && printers.length > 0,
    },
    // PRIORITY 11: No printers detected
    {
      priority: 11,
      message: 'No se detectaron impresoras.',
      onClick: () => null,
      type: ERROR_TYPES_MAP.error.key,
      show: isElectronInstance && printers.length === 0,
    },
    // PRIORITY 12: Address updated (INFO)
    {
      priority: 12,
      message: 'Dirección actualizada. Revísala aquí.',
      onClick: () => navigate('/datos-del-comercio/ubicacion'),
      type: ERROR_TYPES_MAP.info.key,
      show: Boolean(commerce.direccion) && setupStatus.address.isComplete,
    },
  ];

  const visibleWarnings = allWarnings.filter((w) => w.show);
  const highestPriorityWarning = visibleWarnings.length > 0 ? visibleWarnings[0] : null;

  return {
    warning: highestPriorityWarning,
  };
};

/**
 * StatusBottomBarMobile - Versión móvil colapsable de la barra de notificaciones
 *
 * Características:
 * - Colapsable: Usuario puede ocultar/mostrar la barra
 * - Tab flotante: Cuando está oculta, muestra un tab para expandir
 * - Mensajes cortos: Textos optimizados para pantallas pequeñas
 * - Animaciones suaves: Transiciones CSS para mejor UX
 * - Persistencia: Guarda el estado en localStorage
 * @returns {JSX.Element | null} Componente de barra de notificaciones móvil o null si no hay warnings
 */
export const StatusBottomBarMobile = () => {
  const { warning } = useStatus();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Recuperar estado desde localStorage
    const saved = localStorage.getItem('statusBarMobileCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  // Si no hay warning, no mostrar nada
  if (!warning) return null;

  const handleToggle = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    // Guardar en localStorage
    localStorage.setItem('statusBarMobileCollapsed', JSON.stringify(newState));
  };

  const handleWarningClick = () => {
    warning.onClick();
  };

  return (
    <>
      {/* Barra principal - se oculta cuando isCollapsed=true */}
      <div
        className={`
          fixed top-0 left-0 w-full z-[999]
          transition-transform duration-300 ease-in-out
          ${isCollapsed ? '-translate-y-full' : 'translate-y-0'}
          shadow-lg
        `}
      >
        <div
          className={`
            flex items-center gap-x-2 px-4 py-3
            text-color-primary_dark text-xs
          `}
          style={{
            backgroundColor: warning.bgColor || ERROR_TYPES_MAP[warning.type].color,
          }}
        >
          {/* Icono */}
          <div className="flex-shrink-0 flex items-center justify-center" onClick={handleWarningClick}>
            {!warning.hideIcon && warning?.emoji ? (
              // Emoji personalizado (Tabler icon o string)
              typeof warning.emoji === 'string' ? (
                <span className="text-xl">{warning.emoji}</span>
              ) : (
                <div className="flex items-center justify-center">{warning.emoji}</div>
              )
            ) : !warning.hideIcon ? (
              // Ícono por defecto según el tipo (Tabler icon)
              <div className="flex items-center justify-center">{ERROR_TYPES_MAP[warning.type].icon}</div>
            ) : null}
          </div>

          {/* Mensaje - clickeable */}
          <div className="flex-1 font-semibold cursor-pointer" onClick={handleWarningClick}>
            {warning.message}
          </div>

          {/* Botón de colapsar */}
          <button
            onClick={handleToggle}
            className="flex-shrink-0 p-1 hover:bg-white/10 rounded-full transition-colors"
            aria-label="Ocultar notificación"
          >
            <IconChevronUp size={20} />
          </button>
        </div>
      </div>

      {/* Tab flotante - se muestra cuando isCollapsed=true */}
      <button
        onClick={handleToggle}
        className={`
          fixed top-0 z-[998]
          px-4 py-2 rounded-b-lg
          text-xs font-bold tracking-wider
          border border-t-0
          shadow-md
          transition-all duration-300 ease-in-out
          ${isCollapsed ? 'translate-y-0 opacity-100' : '-translate-y-full opacity-0 pointer-events-none'}
        `}
        style={{
          backgroundColor: plickTheme.colors.warning,
          color: plickTheme.colors.primaryDark,
          borderColor: plickTheme.colors.warning,
          left: '50%',
          transform: isCollapsed ? 'translateX(-40%)' : 'translateX(-40%) translateY(-100%)',
        }}
        aria-label="Mostrar notificación"
      >
        <div className="flex items-center gap-2">
          <span>NOTIFICACIÓN</span>
          <IconChevronDown size={16} />
        </div>
      </button>
    </>
  );
};

export default StatusBottomBarMobile;
